import { useQuery } from '@tanstack/react-query'
import { RowWindow } from '@/components/RowWindow'
import { HandCoins, ReceiptText, TriangleAlert } from 'lucide-react'
import { apiClient } from '@/lib/api'
import { formatUsd } from '@/lib/money'
import { Badge, EmptyState } from '@/components/ui/primitives'
import { t } from '@/i18n'

interface AgeingBucket {
  label: string
  amountUsd: number
  customerCount: number
}

interface PayableRow {
  kind: 'Delivery' | 'Expense'
  id: number
  reference: string
  description: string
  supplierName: string | null
  incurredOn: string
  dueOn: string | null
  amountUsd: number
  ageDays: number
  isOverdue: boolean
}

interface Payables {
  totalUsd: number
  deliveriesUsd: number
  expensesUsd: number
  overdueUsd: number
  count: number
  buckets: AgeingBucket[]
  bySupplier: { supplierName: string; count: number; amountUsd: number }[]
  rows: PayableRow[]
}

interface Receivables {
  totalOutstandingUsd: number
  customerCount: number
  buckets: AgeingBucket[]
  customers: {
    id: number
    name: string
    phone: string | null
    balanceUsd: number
    creditLimitUsd: number
    isOverLimit: boolean
    oldestUnpaidDays: number | null
  }[]
}

/** A figure with its label, for the row of totals at the top of each panel. */
function Total({
  label,
  value,
  tone,
}: {
  label: string
  value: string
  tone?: 'bad' | 'plain'
}) {
  return (
    <div className="surface-raised rounded-[var(--radius-card)] border p-4">
      <p className="text-xs text-[var(--text-secondary)]">{label}</p>
      <p
        className={`tabular mt-1 text-xl font-semibold ${
          tone === 'bad' ? 'text-danger-500' : ''
        }`}
      >
        {value}
      </p>
    </div>
  )
}

/**
 * The ageing ladder, as a row of rungs.
 *
 * The same four bands on both sides, because the question is the same one asked from either end
 * of the counter and a shopkeeper comparing them should not have to translate.
 */
function Ageing({ buckets }: { buckets: AgeingBucket[] }) {
  const total = buckets.reduce((sum, bucket) => sum + bucket.amountUsd, 0)

  return (
    <div className="surface-raised rounded-[var(--radius-card)] border p-4">
      <p className="mb-3 text-sm font-medium">{t('How old it is')}</p>
      <ul className="grid gap-3 sm:grid-cols-4">
        {buckets.map((bucket) => (
          <li key={bucket.label}>
            <p className="text-xs text-[var(--text-secondary)]">{bucket.label}</p>
            <p className="tabular mt-0.5 font-semibold">{formatUsd(bucket.amountUsd)}</p>
            <div className="surface-sunken mt-1.5 h-1.5 overflow-hidden rounded-full">
              <div
                className="h-full rounded-full bg-[var(--accent)]"
                style={{ width: `${total > 0 ? (bucket.amountUsd / total) * 100 : 0}%` }}
              />
            </div>
          </li>
        ))}
      </ul>
    </div>
  )
}

/**
 * What the shop owes.
 *
 * Deliveries taken on credit and bills not yet paid, on one list — because what makes something a
 * payable is not what it was bought for, it is that the money has not left yet.
 */
export function PayablesPanel({ branchId }: { branchId?: number | null }) {
  const payables = useQuery({
    queryKey: ['payables', branchId ?? null],
    queryFn: () =>
      apiClient.get<Payables>(
        `/api/reports/payables${branchId ? `?branchId=${branchId}` : ''}`,
      ),
  })

  if (!payables.data) return null

  if (payables.data.count === 0) {
    return (
      <EmptyState
        icon={ReceiptText}
        title={t('You owe nothing')}
        description={t('Deliveries taken on credit and unpaid bills appear here until they are settled.')}
      />
    )
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="grid gap-3 sm:grid-cols-4">
        <Total label={t('Owed in total')} value={formatUsd(payables.data.totalUsd)} />
        <Total label={t('Deliveries')} value={formatUsd(payables.data.deliveriesUsd)} />
        <Total label={t('Bills and services')} value={formatUsd(payables.data.expensesUsd)} />
        <Total
          label={t('Overdue')}
          value={formatUsd(payables.data.overdueUsd)}
          tone={payables.data.overdueUsd > 0 ? 'bad' : 'plain'}
        />
      </div>

      <Ageing buckets={payables.data.buckets} />

      <section className="surface-raised rounded-[var(--radius-card)] border">
        <p className="border-b border-[var(--border-subtle)] p-3 text-sm font-medium">
          {t('Who you owe')}
        </p>
        <ul className="flex flex-col">
          <RowWindow rows={payables.data.bySupplier}>{(supplier) => (
            <li
              key={supplier.supplierName}
              className="flex items-center justify-between gap-3 border-b border-[var(--border-subtle)] px-3 py-2 text-sm last:border-0"
            >
              <span className="truncate">{supplier.supplierName}</span>
              <span className="tabular shrink-0 font-medium">
                {formatUsd(supplier.amountUsd)}
                <span className="ml-2 text-xs font-normal text-[var(--text-secondary)]">
                  {supplier.count} {supplier.count === 1 ? 'bill' : 'bills'}
                </span>
              </span>
            </li>
          )}</RowWindow>
        </ul>
      </section>

      <section className="surface-raised rounded-[var(--radius-card)] border">
        <p className="border-b border-[var(--border-subtle)] p-3 text-sm font-medium">
          {t('Every unpaid bill, oldest first')}
        </p>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-[var(--border-subtle)] text-xs text-[var(--text-secondary)]">
                <th className="px-3 py-2 text-left font-medium">{t('What')}</th>
                <th className="px-3 py-2 text-left font-medium">{t('Who')}</th>
                <th className="px-3 py-2 text-left font-medium">{t('Since')}</th>
                <th className="px-3 py-2 text-right font-medium">{t('Amount')}</th>
              </tr>
            </thead>
            <tbody>
              <RowWindow rows={payables.data.rows} colSpan={5}>{(row) => (
                <tr
                  key={`${row.kind}-${row.id}`}
                  className="border-b border-[var(--border-subtle)] last:border-0"
                >
                  <td className="px-3 py-2">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="tabular font-medium">{row.reference}</span>
                      <Badge tone="neutral">
                        {row.kind === 'Delivery' ? 'Delivery' : 'Bill'}
                      </Badge>
                      {row.isOverdue && <Badge tone="danger">{t('Overdue')}</Badge>}
                    </div>
                    <p className="truncate text-xs text-[var(--text-secondary)]">
                      {row.description}
                    </p>
                  </td>
                  <td className="px-3 py-2">{row.supplierName ?? '—'}</td>
                  <td className="tabular whitespace-nowrap px-3 py-2">
                    {row.incurredOn.slice(0, 10)}
                    <span className="block text-xs text-[var(--text-secondary)]">
                      {row.ageDays} {row.ageDays === 1 ? 'day' : 'days'}
                      {row.dueOn ? ` · due ${row.dueOn.slice(0, 10)}` : ''}
                    </span>
                  </td>
                  <td className="tabular whitespace-nowrap px-3 py-2 text-right font-medium">
                    {formatUsd(row.amountUsd)}
                  </td>
                </tr>
              )}</RowWindow>
            </tbody>
          </table>
        </div>
      </section>

      <p className="text-xs text-[var(--text-secondary)]">
        Settle a delivery from Inventory → Deliveries, and a bill from Expenses. Paying takes the
        money out of wherever you say it comes from.
      </p>
    </div>
  )
}

/**
 * What the shop is owed.
 *
 * Every sale put on a customer's account, however it was described when it was made — an invoice,
 * a standing arrangement, a regular who pays at the end of the month. In this system they are one
 * thing: goods taken now against a credit limit.
 */
export function ReceivablesPanel() {
  const receivables = useQuery({
    queryKey: ['receivables'],
    queryFn: () => apiClient.get<Receivables>('/api/reports/receivables'),
  })

  if (!receivables.data) return null

  if (receivables.data.customerCount === 0) {
    return (
      <EmptyState
        icon={HandCoins}
        title={t('Nobody owes you anything')}
        description={t('Sales put on a customer\'s account appear here until they are paid off.')}
      />
    )
  }

  const overLimit = receivables.data.customers.filter((customer) => customer.isOverLimit)

  return (
    <div className="flex flex-col gap-4">
      <div className="grid gap-3 sm:grid-cols-3">
        <Total label={t('Owed to you')} value={formatUsd(receivables.data.totalOutstandingUsd)} />
        <Total label={t('Accounts')} value={String(receivables.data.customerCount)} />
        <Total
          label={t('Over their limit')}
          value={String(overLimit.length)}
          tone={overLimit.length > 0 ? 'bad' : 'plain'}
        />
      </div>

      <Ageing buckets={receivables.data.buckets} />

      {overLimit.length > 0 && (
        <p className="flex items-start gap-2 rounded-lg bg-warn-500/10 px-3 py-2 text-sm text-warn-500">
          <TriangleAlert className="mt-0.5 h-4 w-4 shrink-0" />
          {overLimit.length} {overLimit.length === 1 ? 'account is' : 'accounts are'} past the
          limit you set. Nothing more can go on those until they pay some of it down.
        </p>
      )}

      <section className="surface-raised rounded-[var(--radius-card)] border">
        <p className="border-b border-[var(--border-subtle)] p-3 text-sm font-medium">
          {t('Who owes you, most first')}
        </p>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-[var(--border-subtle)] text-xs text-[var(--text-secondary)]">
                <th className="px-3 py-2 text-left font-medium">{t('Customer')}</th>
                <th className="px-3 py-2 text-left font-medium">{t('Oldest')}</th>
                <th className="px-3 py-2 text-right font-medium">{t('Limit')}</th>
                <th className="px-3 py-2 text-right font-medium">{t('Owed')}</th>
              </tr>
            </thead>
            <tbody>
              <RowWindow rows={receivables.data.customers} colSpan={5}>{(customer) => (
                <tr
                  key={customer.id}
                  className="border-b border-[var(--border-subtle)] last:border-0"
                >
                  <td className="px-3 py-2">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="font-medium">{customer.name}</span>
                      {customer.isOverLimit && <Badge tone="danger">{t('Over limit')}</Badge>}
                    </div>
                    {customer.phone && (
                      <p className="tabular text-xs text-[var(--text-secondary)]">
                        {customer.phone}
                      </p>
                    )}
                  </td>
                  <td className="tabular whitespace-nowrap px-3 py-2">
                    {customer.oldestUnpaidDays === null
                      ? '—'
                      : `${customer.oldestUnpaidDays} ${
                          customer.oldestUnpaidDays === 1 ? 'day' : 'days'
                        }`}
                  </td>
                  <td className="tabular whitespace-nowrap px-3 py-2 text-right text-[var(--text-secondary)]">
                    {formatUsd(customer.creditLimitUsd)}
                  </td>
                  <td className="tabular whitespace-nowrap px-3 py-2 text-right font-medium">
                    {formatUsd(customer.balanceUsd)}
                  </td>
                </tr>
              )}</RowWindow>
            </tbody>
          </table>
        </div>
      </section>

      <p className="text-xs text-[var(--text-secondary)]">
        Take a payment against an account from Customers. What is owed here is the same figure the
        till checks a credit limit against.
      </p>
    </div>
  )
}
